import { useState } from 'react';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: ReactNode;
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: "Who is OverUnder Roofing for?",
      answer: (
        <div className="text-base text-gray-700 leading-relaxed">
          <p className="mb-3">This service is a great fit if:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
              <span>You want multiple bids but don't trust them or know how to compare them properly</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
              <span>You're concerned about overpaying or getting poor-quality work</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
              <span>You don't have the time or expertise to vet contractors and manage the process yourself</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      question: "What's included in the $500 fee?",
      answer: (
        <p className="text-base text-gray-700 leading-relaxed">
          The $500 flat fee covers the entire{' '}
          <Link to="/overunder-method" className="text-emerald-700 font-medium hover:text-emerald-800 hover:underline transition-colors cursor-pointer">
            The OverUnder Method™
          </Link>{' '}
          bidding process: project intake, contractor outreach, bid collection and coordination, detailed bid analysis, quality comparisons, and our expert recommendation. You also get post-job support to ensure everything goes smoothly. This fee is due upfront and is non-refundable, but you're under no obligation to proceed with any bid.
        </p>
      )
    },
    {
      question: "How do you vet contractors?",
      answer: "Every contractor in our network is licensed, insured, and bonded in North Carolina. We personally verify their credentials, check references, review past work, and assess their reputation in the community. We only work with contractors who have a proven track record of quality work, fair pricing, and excellent customer service. We also continuously monitor feedback from homeowners we've worked with."
    },
    {
      question: "Can I use my own contractor recommendation?",
      answer: "Absolutely! In fact, we encourage it. If you have a contractor you already trust or one that's been recommended by a friend, we'll coordinate their bid alongside the others. This gives you a true apples-to-apples comparison and ensures you're making the most informed decision. There's no extra charge for including your own pick."
    },
    {
      question: "What if I don't like any of the bids?",
      answer: "You're never obligated to move forward with any bid. If none of the options meet your expectations, we can discuss next steps—whether that's adjusting project scope, waiting for better timing, or exploring additional contractors. The $500 fee covers the work we've already done, but we'll do our best to help you find a solution that works."
    },
    {
      question: "Do you work with insurance claims?",
      answer: "Yes, we can help coordinate bids for insurance-related roof repairs or replacements. However, we don't handle insurance negotiations directly. We recommend working with a public adjuster if you're dealing with a complex claim. We'll provide detailed bid documentation that you can submit to your insurance company for reimbursement."
    },
    {
      question: "What areas do you serve?",
      answer: "We serve Wake County and the Triangle area, including Raleigh, Cary, Apex, Wake Forest, Holly Springs, Fuquay-Varina, Garner, and surrounding communities in North Carolina. If you're outside this area but still nearby, reach out—we may still be able to help depending on your location and project scope."
    },
    {
      question: "How long does the bidding process take?",
      answer: "From intake to final bid presentation, the process typically takes 1-2 weeks. This includes time for us to reach out to contractors, schedule site visits, collect detailed bids, and prepare our analysis. If you're on a tight timeline, let us know and we'll do our best to expedite the process."
    },
    {
      question: "What's the success fee structure?",
      answer: "After the $500 flat fee, we charge a success fee only if you move forward with a contractor. This fee is the lesser of 5% of the total job cost OR 25% of the savings we help you achieve (compared to the highest bid). This aligns our incentives with yours—we only earn more if we save you money. If you don't proceed with any bid, there's no additional fee."
    },
    {
      question: "Are you licensed contractors?",
      answer: "No, we are not licensed contractors. We're independent roofing advocates and consultants. We don't perform roofing work ourselves—we coordinate bids from licensed, insured contractors and provide unbiased guidance. All actual roofing work is performed by fully licensed North Carolina contractors."
    },
    {
      question: "What happens after I choose a contractor?",
      answer: "Once you select a contractor, we'll help facilitate the contract signing and project kickoff. We remain available throughout the project to answer questions, address concerns, and ensure everything stays on track. If any issues arise, we'll work with you and the contractor to resolve them quickly."
    },
    {
      question: "Is hiring a roofing consultant worth it?",
      answer: "For most homeowners, yes. Roofing quotes can vary by thousands of dollars with no clear explanation. A consultant helps you compare bids properly, avoid overpaying, and reduce the risk of poor workmanship — often saving more than the consulting fee."
    },
    {
      question: "Why do roofing quotes vary so much?",
      answer: "Roofing bids often differ due to variations in materials, scope, labor assumptions, and hidden exclusions. Two quotes may look similar in price but include very different levels of quality and protection."
    },
    {
      question: "Why isn't getting 2–3 bids enough?",
      answer: "Getting multiple bids is a good start — but without expert analysis, you're still guessing. Most homeowners don't have the experience to evaluate scope differences, material quality, or pricing strategies."
    },
    {
      question: "How much can you actually save using OverUnder Roofing?",
      answer: "Savings vary by project, but many homeowners avoid overpaying by thousands of dollars or secure higher-quality materials for the same price. Our goal is to help you find the best value — not just the lowest price."
    },
    {
      question: "Who is this service NOT ideal for?",
      answer: (
        <div className="text-base text-gray-700 leading-relaxed">
          <p className="mb-3">This may not be the best fit if:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
              <span>You already have a contractor you fully trust and are happy with their quote</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
              <span>You prefer to handle the entire bidding and hiring process yourself</span>
            </li>
          </ul>
        </div>
      )
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="bg-white py-24">
      <div className="max-w-4xl mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-3">Common Questions</h2>
          <p className="text-base text-gray-600">Everything you need to know about our process</p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-emerald-300 transition-colors"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-amber-50/30 transition-colors"
              >
                <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h3>
                <i className={`${openIndex === index ? 'ri-close-line' : 'ri-add-line'} text-2xl text-emerald-700 flex-shrink-0 transition-transform`}></i>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-6 pt-0">
                  {typeof faq.answer === 'string' ? (
                    <p className="text-base text-gray-700 leading-relaxed">{faq.answer}</p>
                  ) : (
                    faq.answer
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}